'use client'

import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import {
  Menu, X, Sun, Moon,
  Calendar, ShoppingBag, LayoutGrid, Globe, Code, Star, BookOpen, Wallet,
  ChevronDown,
  type LucideIcon,
} from 'lucide-react'
import { RezaroLogo } from './rezaro-logo'
import { LanguageSwitcher } from './language-switcher'
import { lp } from '../_home/locale-path'

type Lang = 'de' | 'en' | 'tr'

interface MegaItem { label: string; href: string; desc: string; icon: LucideIcon }

// Per-locale UI dictionary. The 'de' strings are byte-identical to the
// original hardcoded German so the live German site is unchanged.
const NAV_T = {
  de: {
    reservierungen: 'Reservierungen',
    planung: 'Planung',
    marketing: 'Marketing',
    preise: 'Preise',
    blog: 'Blog',
    hilfe: 'Hilfe',
    faq: 'FAQ',
    useCases: 'Use Cases',
    anmelden: 'Anmelden',
    kostenlosTesten: 'Kostenlos testen',
    sprache: 'Sprache:',
    themeWechseln: 'Theme wechseln',
    menueOeffnen: 'Menü öffnen',
    features: [
      { desc: 'Online-Reservierungen 24/7', label: 'Reservierungen' },
      { desc: 'Schluss mit Klemmbrett', label: 'Digitales Reservierungsbuch' },
      { desc: 'Code-Snippet für Ihre Website', label: 'Reservierungs-Widget' },
      { desc: 'Lieferung, Abholung, QR-Code', label: 'Bestellungen' },
    ],
    planningItems: [
      { desc: 'Übersicht aller Planungs-Funktionen', label: 'Planung' },
      { desc: 'Visueller Plan, Drag-and-Drop', label: 'Tischplan' },
      { desc: 'No-Shows verhindern', label: 'Anzahlungen' },
    ],
    marketingItems: [
      { desc: 'Eigene Mini-Website mit SEO', label: 'Restaurant-Website' },
      { desc: 'Mehr 5-Sterne-Bewertungen', label: 'Gastbewertungen' },
    ],
  },
  en: {
    reservierungen: 'Reservations',
    planung: 'Planning',
    marketing: 'Marketing',
    preise: 'Pricing',
    blog: 'Blog',
    hilfe: 'Help',
    faq: 'FAQ',
    useCases: 'Use cases',
    anmelden: 'Sign in',
    kostenlosTesten: 'Start for free',
    sprache: 'Language:',
    themeWechseln: 'Switch theme',
    menueOeffnen: 'Open menu',
    features: [
      { desc: 'Online reservations 24/7', label: 'Reservations' },
      { desc: 'No more clipboards', label: 'Digital reservation book' },
      { desc: 'Code snippet for your website', label: 'Reservation widget' },
      { desc: 'Delivery, pickup, QR code', label: 'Orders' },
    ],
    planningItems: [
      { desc: 'Overview of all planning features', label: 'Planning' },
      { desc: 'Visual floor plan, drag-and-drop', label: 'Table plan' },
      { desc: 'Prevent no-shows', label: 'Deposits' },
    ],
    marketingItems: [
      { desc: 'Your own mini website with SEO', label: 'Restaurant website' },
      { desc: 'More 5-star reviews', label: 'Guest reviews' },
    ],
  },
  tr: {
    reservierungen: 'Rezervasyonlar',
    planung: 'Planlama',
    marketing: 'Pazarlama',
    preise: 'Fiyatlar',
    blog: 'Blog',
    hilfe: 'Yardım',
    faq: 'SSS',
    useCases: 'Kullanım senaryoları',
    anmelden: 'Giriş yap',
    kostenlosTesten: 'Ücretsiz başla',
    sprache: 'Dil:',
    themeWechseln: 'Temayı değiştir',
    menueOeffnen: 'Menüyü aç',
    features: [
      { desc: '7/24 online rezervasyon', label: 'Rezervasyonlar' },
      { desc: 'Kağıt panoya elveda', label: 'Dijital rezervasyon defteri' },
      { desc: 'Web siteniz için kod parçacığı', label: 'Rezervasyon aracı' },
      { desc: 'Teslimat, gel-al, QR kod', label: 'Siparişler' },
    ],
    planningItems: [
      { desc: 'Tüm planlama özelliklerine genel bakış', label: 'Planlama' },
      { desc: 'Görsel masa planı, sürükle-bırak', label: 'Masa planı' },
      { desc: 'Gelmeyenleri önleyin', label: 'Kapora' },
    ],
    marketingItems: [
      { desc: 'SEO ile kendi mini web siteniz', label: 'Restoran web sitesi' },
      { desc: 'Daha fazla 5 yıldızlı değerlendirme', label: 'Misafir değerlendirmeleri' },
    ],
  },
} satisfies Record<Lang, Record<string, unknown>>

// Static structure (href + icon) per mega-menu group. Labels and descriptions
// are merged in from the locale dictionary at render time.
const FEATURES_META = [
  { href: '/reservierungen', icon: Calendar },
  { href: '/reservierungen/digitales-reservierungsbuch', icon: BookOpen },
  { href: '/reservierungs-tool', icon: Code },
  { href: '/bestellungen', icon: ShoppingBag },
]

const PLANNING_META = [
  { href: '/planung', icon: LayoutGrid },
  { href: '/planung/tischplan', icon: LayoutGrid },
  { href: '/planung/anzahlung', icon: Wallet },
]

const MARKETING_META = [
  { href: '/restaurant-webseite', icon: Globe },
  { href: '/marketing/gastbewertungen', icon: Star },
]

function mergeItems(
  meta: { href: string; icon: LucideIcon }[],
  text: { label: string; desc: string }[],
  lang: Lang,
): MegaItem[] {
  return meta.map((m, i) => ({ href: lp(m.href, lang), icon: m.icon, label: text[i].label, desc: text[i].desc }))
}

export function PublicNav({ lang = 'de' }: { lang?: Lang } = {}) {
  const tt = NAV_T[lang]
  const FEATURES = mergeItems(FEATURES_META, tt.features, lang)
  const PLANNING = mergeItems(PLANNING_META, tt.planningItems, lang)
  const MARKETING = mergeItems(MARKETING_META, tt.marketingItems, lang)

  const [open, setOpen] = useState(false)
  const [theme, setTheme] = useState<'light' | 'dark'>('light')
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    // Follow the OS preference and keep following it live. The manual
    // toggle below is a session-only override (not persisted), so the
    // site never gets stuck in dark from a previous visit.
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const apply = (dark: boolean) => {
      const t = dark ? 'dark' : 'light'
      setTheme(t)
      document.documentElement.setAttribute('data-theme', t)
    }
    apply(mq.matches)
    const onChange = (e: MediaQueryListEvent) => apply(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  function toggleTheme() {
    // Session-only: applies immediately but is NOT saved, so the next
    // visit again follows the device setting.
    const next = theme === 'light' ? 'dark' : 'light'
    setTheme(next)
    document.documentElement.setAttribute('data-theme', next)
  }

  function openMega(name: string) {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    setOpenMenu(name)
  }
  function scheduleClose() {
    if (closeTimer.current) clearTimeout(closeTimer.current)
    closeTimer.current = setTimeout(() => setOpenMenu(null), 150)
  }

  return (
    <header className="sticky top-0 z-50 border-b border-pub-rule bg-pub-surface/90 backdrop-blur-md"
      style={{ fontFamily: 'var(--font-pub)' }}>
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">

        <RezaroLogo size="sm" href={lp('/', lang)} />

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 md:flex">
          {[
            { name: tt.reservierungen, items: FEATURES },
            { name: tt.planung, items: PLANNING },
            { name: tt.marketing, items: MARKETING },
          ].map((m) => (
            <div key={m.name}
              className="relative"
              onMouseEnter={() => openMega(m.name)}
              onMouseLeave={scheduleClose}>
              <button type="button"
                className="flex items-center gap-1 px-3 py-2 text-[13px] font-medium text-pub-muted hover:text-pub-ink transition-colors">
                {m.name}
                <ChevronDown className={`h-3 w-3 transition-transform ${openMenu === m.name ? 'rotate-180' : ''}`} />
              </button>
              {openMenu === m.name && <MegaMenu items={m.items} />}
            </div>
          ))}

          <Link href={lp('/pricing', lang)}
            className="px-3 py-2 text-[13px] font-medium text-pub-muted hover:text-pub-ink transition-colors">
            {tt.preise}
          </Link>
          <Link href="/blog"
            className="px-3 py-2 text-[13px] font-medium text-pub-muted hover:text-pub-ink transition-colors">
            {tt.blog}
          </Link>
          <Link href="/hilfe"
            className="px-3 py-2 text-[13px] font-medium text-pub-muted hover:text-pub-ink transition-colors">
            {tt.hilfe}
          </Link>
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <LanguageSwitcher current={lang} />
          <button type="button" onClick={toggleTheme} aria-label={tt.themeWechseln}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-pub-muted hover:bg-pub-accent-bg hover:text-pub-accent transition-colors">
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <Link href={lp('/signin', lang)}
            className="text-[13px] font-medium text-pub-muted hover:text-pub-ink transition-colors px-1">
            {tt.anmelden}
          </Link>
          <Link href={lp('/signup', lang)}
            className="rounded-full bg-pub-accent px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-pub-accent-dark">
            {tt.kostenlosTesten}
          </Link>
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <button type="button" onClick={toggleTheme} aria-label={tt.themeWechseln}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-pub-muted hover:bg-pub-accent-bg transition-colors">
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <button type="button"
            className="rounded p-2 text-pub-muted hover:bg-pub-accent-bg"
            onClick={() => setOpen(!open)} aria-label={tt.menueOeffnen}>
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="border-t border-pub-rule bg-pub-surface px-6 py-5 md:hidden max-h-[80vh] overflow-y-auto">
          <nav className="flex flex-col gap-1">
            <MobileSection label={tt.reservierungen} items={FEATURES} onClick={() => setOpen(false)} />
            <MobileSection label={tt.planung} items={PLANNING} onClick={() => setOpen(false)} />
            <MobileSection label={tt.marketing} items={MARKETING} onClick={() => setOpen(false)} />
            <Link href={lp('/pricing', lang)} onClick={() => setOpen(false)}
              className="py-2.5 text-[14px] font-medium text-pub-ink">{tt.preise}</Link>
            <Link href={lp('/use-cases', lang)} onClick={() => setOpen(false)}
              className="py-2.5 text-[14px] font-medium text-pub-ink">{tt.useCases}</Link>
            <Link href="/blog" onClick={() => setOpen(false)}
              className="py-2.5 text-[14px] font-medium text-pub-ink">{tt.blog}</Link>
            <Link href="/hilfe" onClick={() => setOpen(false)}
              className="py-2.5 text-[14px] font-medium text-pub-ink">{tt.hilfe}</Link>
            <Link href={lp('/faq', lang)} onClick={() => setOpen(false)}
              className="py-2.5 text-[14px] font-medium text-pub-ink">{tt.faq}</Link>
            <div className="my-2 border-t border-pub-rule" />
            <div className="flex items-center gap-2 py-1">
              <span className="text-[12px] text-pub-muted">{tt.sprache}</span>
              <Link href="/" hrefLang="de" onClick={() => setOpen(false)} className="rounded-full border border-pub-accent/40 px-2.5 py-1 text-[12px] font-semibold text-pub-accent">DE</Link>
              <Link href="/en" hrefLang="en" onClick={() => setOpen(false)} className="rounded-full border border-pub-rule px-2.5 py-1 text-[12px] font-medium text-pub-muted">EN</Link>
              <Link href="/tr" hrefLang="tr" onClick={() => setOpen(false)} className="rounded-full border border-pub-rule px-2.5 py-1 text-[12px] font-medium text-pub-muted">TR</Link>
            </div>
            <div className="my-2 border-t border-pub-rule" />
            <Link href={lp('/signin', lang)} onClick={() => setOpen(false)}
              className="rounded-full border border-pub-accent px-4 py-2.5 text-center text-[13px] font-semibold text-pub-accent hover:bg-pub-accent/5 transition-colors">
              {tt.anmelden}
            </Link>
            <Link href={lp('/signup', lang)} onClick={() => setOpen(false)}
              className="rounded-full bg-pub-accent px-4 py-2.5 text-center text-[13px] font-semibold text-white hover:bg-pub-accent-dark transition-colors">
              {tt.kostenlosTesten}
            </Link>
          </nav>
        </div>
      )}
    </header>
  )
}

function MegaMenu({ items }: { items: MegaItem[] }) {
  return (
    <div className="absolute left-0 top-full pt-2 w-[420px]">
      <div className="rounded-2xl border border-pub-rule bg-pub-surface shadow-2xl p-2">
        {items.map((item) => (
          <Link key={item.href} href={item.href}
            className="flex items-start gap-3 rounded-lg p-3 transition-colors hover:bg-pub-accent-bg">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-pub-accent-bg">
              <item.icon className="h-4 w-4 text-pub-accent" />
            </div>
            <div>
              <p className="text-[13.5px] font-semibold text-pub-ink">{item.label}</p>
              <p className="mt-0.5 text-[12px] text-pub-muted">{item.desc}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}

function MobileSection({ label, items, onClick }: { label: string; items: MegaItem[]; onClick: () => void }) {
  return (
    <details className="group">
      <summary className="flex cursor-pointer items-center justify-between py-2.5 text-[14px] font-medium text-pub-ink">
        {label}
        <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
      </summary>
      <div className="ml-2 border-l border-pub-rule pl-4 py-1 space-y-1.5">
        {items.map((item) => (
          <Link key={item.href} href={item.href} onClick={onClick}
            className="block py-1.5 text-[13.5px] text-pub-muted hover:text-pub-accent">
            {item.label}
          </Link>
        ))}
      </div>
    </details>
  )
}
